import { useFormik } from 'formik';
import {
  useGetContactCollectionByFirebaseEventIdOrCreateOneQuery,
  useCreateContactsWithoutTagsMutation,
  useGetTelemetryDataForContactCollectionGuestQuery
} from '@graphql/generated';
import { useTranslation, useRouterHelper } from '@shared/core';
import { useContactCollectorGuestTelemetryTelemetry } from '../guest/ContactCollectorGuest.telemetry';
import { contactCollectorGuestRoutePaths } from '../guest';
import { Contacts, initialValues, yupSchema } from '../guest/ContactCollectorGuest.controller';

export const useContactCollectorIframeController = (firebaseEventId: string) => {
  const { trackError } = useContactCollectorGuestTelemetryTelemetry();
  const routeHelpers = useRouterHelper();
  const { t } = useTranslation('contactCollector');
  const tAdmin = t('admin');

  const { data, loading, error } = useGetContactCollectionByFirebaseEventIdOrCreateOneQuery({
    batchMode: 'fast',
    ssr: false,
    variables: {
      firebaseEventId
    },
    onError: error => trackError('Query', error)
  });

  const eventId = data?.getContactCollectionByFirebaseEventIdOrCreateOne?.eventId;

  const { data: telemetryQueryData, loading: isTelemetryQueryLoading } = useGetTelemetryDataForContactCollectionGuestQuery({
    batchMode: 'fast',
    ssr: false,
    variables: {
      eventId: eventId!
    },
    skip: !eventId,
    onError: error => trackError('Query', error)
  });

  const [createContactsMutation, { loading: isCreateContactsMutationLoading, error: createContactsMutationError }] = useCreateContactsWithoutTagsMutation();

  const formik = useFormik<Contacts>({
    initialValues,
    validationSchema: yupSchema(tAdmin.required(), tAdmin.invalidEmail()),
    validateOnMount: true,
    onSubmit: async (values, formikHelpers) => {
      try {
        const contactCollectionId = data?.getContactCollectionByFirebaseEventIdOrCreateOne?.id;

        if (!contactCollectionId) {
          throw Error('No Collection Id provided');
        }

        const mutationData = values.contacts.map((contact, index) => ({
          address:
            // we do not want to save addresses from household members which are not the head of the household
            index === 0
              ? {
                  address1: contact.address1,
                  address2: contact.address2,
                  city: contact.city,
                  country: contact.country,
                  countryCode: contact.countryCode,
                  postalCode: contact.postalCode,
                  region: contact.region,
                  validated: contact.validated
                }
              : undefined,
          contact: {
            contactCollectionId,
            firebaseEventId,
            eventId,
            email: ((contact.email || values.contacts[0].email) ?? '').trim(),
            firstName: (contact.firstName ?? '').trim(),
            lastName: (contact.lastName ?? '').trim(),
            party: values.contacts.length > 1 ? `${contactCollectionId}-${values.contacts[0].firstName}-${values.contacts[0].lastName}` : undefined
          },
          phoneNumbers: contact.phoneNumber
            ? [
                {
                  allowSMS: true,
                  mobile: true,
                  number: contact.phoneNumber.trim()
                }
              ]
            : undefined
        }));
        await createContactsMutation({ variables: { data: mutationData } });
        formikHelpers.resetForm();
        routeHelpers.goToExactRoute(contactCollectorGuestRoutePaths.form.path);
      } catch (error) {
        trackError('Mutation', error);
      }
    }
  });

  return {
    tAdmin,
    showErrorPage: !!error,
    isLoading: loading || isTelemetryQueryLoading,
    formik,
    isMutationLoading: isCreateContactsMutationLoading,
    hasMutationError: !!createContactsMutationError,
    telemetryData: {
      postgresEventId: telemetryQueryData?.eventById?.id ?? '',
      firebaseEventId: telemetryQueryData?.eventById?.firebaseId ?? '',
      joyEventType: telemetryQueryData?.eventById?.info.eventType ?? ''
    }
  };
};
